import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getDashboard } from "../api/smb.api";
import sourceUrl from "../api/Source.api";

const SmbDashboardContext = createContext();

export const SmbDashboardProvider = ({ children, businessId }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [events, setEvents] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    if (!businessId) return;
    try {
      setIsLoading(true);
      const result = await getDashboard(businessId);
      if (result?.status && result?.data) {
        setDashboardData(result.data);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  const connectWebSocket = useCallback(() => {
    if (!businessId) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    // Convert HTTP URL to WebSocket URL
    const wsUrl = sourceUrl.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/smb/businesses/${businessId}/dashboard/ws?token=${token}`);

    ws.onopen = () => {
      setWsConnected(true);
      console.log("Dashboard WebSocket connected");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setEvents((prev) => [data, ...prev].slice(0, 50));

        // Refresh dashboard data on significant events
        if (["call_completed", "lead_created", "content_published"].includes(data.type)) {
          fetchDashboard();
        }
      } catch (err) {
        console.error("WebSocket message parse error:", err);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      console.log("Dashboard WebSocket disconnected, reconnecting in 5s...");
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };

    ws.onerror = (err) => {
      console.error("Dashboard WebSocket error:", err);
      ws.close();
    };

    wsRef.current = ws;
  }, [businessId, fetchDashboard]);

  useEffect(() => {
    fetchDashboard();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [fetchDashboard, connectWebSocket]);

  return (
    <SmbDashboardContext.Provider
      value={{
        dashboardData,
        events,
        wsConnected,
        isLoading,
        refreshDashboard: fetchDashboard,
      }}
    >
      {children}
    </SmbDashboardContext.Provider>
  );
};

export const useSmbDashboard = () => useContext(SmbDashboardContext);
export default SmbDashboardContext;
